/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { type ValidationErrors, type ValidatorFn, Validators } from '@angular/forms';
import {
type Command, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, UNPLACEHOLDER,
} from '@dm/core';
import { ToolService } from 'app/common/services';
import { ServiceAd, Vstore } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { ConfigureDnsComponent } from 'app/feature/service/v-store-service/v-store/detail-v-store/detail-service/dns/configure-dns/configure-dns.component';
import { FileDnsServiceService } from 'app/feature/settings/file-storage-service/file-dns-service/file-dns-service.service';
import { type Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { ServiceAdService } from '../service-ad.service';
import { DomainStatusE, ServiceAdStrService } from '../service-ad-str.service';

@Component({
    selector: 'app-configure-service-ad',
    templateUrl: './configure-service-ad.component.html',
    styleUrls: ['./configure-service-ad.component.scss'],
})
export class ConfigureServiceAdComponent extends FormSlideWindowAbstract implements OnInit {
    /**
     * 是否展示极简样式
     */
    @Input() simpleModeStyle = false;
    @Input() vstore: Vstore;
    @Input() data: ServiceAd;

    i18n = this.str.i18n;

    UNPLACEHOLDER = UNPLACEHOLDER;
    DomainStatusE = DomainStatusE;
    SWITCH = SWITCH;

    pageReady = true;
    windowVisible = true;
    loading = false;
    loadFailed = false;

    vm: FormGroup;
    errorMessage: any;

    // IP或域名校验器
    ipOrDomainValidator: ValidatorFn = DMValidators.or([DMValidators.ipv4, DMValidators.strictDomain]);

    vstoreId: string;
    hasDns = false;
    // 默认系统租户
    systemVstoreId = '0';

    // 表单只初始化一次
    isInited = false;

    isSimpleMode: boolean;

    constructor(
        injector: Injector,
        public str: ServiceAdStrService,
        private service: ServiceAdService,
        public fileDnsService: FileDnsServiceService,
        private toolService: ToolService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.isSimpleMode = SWITCH.IS_SIMPLE_MODE || this.simpleModeStyle;
        this.initVstoreId();
        this.getData();
    }

    refresh(): void {
        this.getData();
    }

    initVstoreId(): string {
        if (this.data) {
            return this.vstoreId = this.data.vstoreId;
        }

        if (this.vstore) {
            return this.vstoreId = this.vstore.ID;
        }

        return this.vstoreId = this.systemVstoreId;
    }

    /**
     * 初始化表单
     */
    initVm(): void {
        const data = this.data;
        const i18n = this.i18n;

        this.vm = new FormGroup({
            ADMINNAME: new FormControl(data.ADMINNAME || '', [
                Validators.required,
                DMValidators.rangeByteLength(1, 63),
            ]),
            ADMINPWD: new FormControl(data.ADMINPWD || '', [
                Validators.required,
                DMValidators.rangeByteLength(1, 127),
            ]),
            FULLDOMAINNAME: new FormControl(data.FULLDOMAINNAME || '', [
                DMValidators.rangeByteLength(1, 127),
            ]),
            OU: new FormControl(data.OU || '', [
                DMValidators.rangeByteLength(0, 255),
            ]),
            SYSTEMNAME: new FormControl(data.SYSTEMNAME || '', [
                DMValidators.rangeByteLength(1, 15),
                this.systemNameValidator,
            ]),
            ISENABLEOVERWRITESAMEACCOUNT: new FormControl(data.ISENABLEOVERWRITESAMEACCOUNT === 'true', [
            ]),
        }, {
            updateOn: 'blur',
        });

        this.errorMessage = {
            ADMINNAME: {
                required: this.toolService.requiredErr(i18n.domainUserNameLab),
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 63),
            },
            ADMINPWD: {
                required: this.toolService.requiredErr(i18n.passwordLab),
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 127),
            },
            FULLDOMAINNAME: {
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 127),
            },
            OU: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
            },
            SYSTEMNAME: {
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 15),
                systemNameFormat: this.str.i18n.systemNameFormat,
            },
        };
    }

    /**
     * 域名校验器。
     * 1、长度范围为1～63个字符，由字母、数字、“_”、“-”组成，不能以“_”或“-”开头和结尾。
     * 2、可以包含多级子域名，各级子域名之间用“.”隔开，“.”不能位于开头和结尾。
     */
    domainValidator = (control: FormControl): ValidationErrors | null => {
        const domain: string = control.value;

        if (!domain) {
            return null;
        }

        // 验证格式是否正确
        const reg = /^([a-zA-Z0-9\-\_\.]){0,63}$/;
        if (!reg.test(domain)) {
            return { domain: true };
        }

        let valid = true;

        // 不能以以下字符开头或结尾
        const invalidLetters = ['_', '-', '.'];
        if (invalidLetters.includes(domain[0]) || invalidLetters.includes(domain.slice(-1))) {
            valid = false;
        }

        return valid ? null : { domain: true };
    };

    /**
     * 系统名称校验
     */
    systemNameValidator = (control: FormControl): ValidationErrors => {
        // eslint-disable-next-line no-control-regex
        const rule = /[\"\/\\\]\[\:\;\|\=\,\+\*\?\<\>\@\(\)\#\x00-\x1F\x7F-\x9F]+/;
        const value: string = control.value;

        // 不能为@#*()=+[]|;:",<>\/?以及控制字符
        if (rule.test(value)) {
            return { systemNameFormat: true };
        }
        return null;
    };

    /**
     * 查询AD域
     */
    getData(): any {
        this.loading = true;
        this.loadFailed = false;

        return forkJoin({
            ad: this.service.get(this.vstoreId),
            hasDns: this.checkDnsConfigure(this.vstoreId),
        }).subscribe(({ ad, hasDns }) => {
            this.data = ad.data;
            this.hasDns = hasDns;

            // 只初始化一次
            if (!this.isInited) {
                super.ngOnInit();
                this.isInited = true;
            }
            // 已加入状态仅支持【输入用户名和密码】退出
            if (this.data.DOMAINSTATUS === DomainStatusE.Join) {
                this.vm.get('FULLDOMAINNAME').disable();
                this.vm.get('OU').disable();
                this.vm.get('SYSTEMNAME').disable();
                this.vm.get('ISENABLEOVERWRITESAMEACCOUNT').disable();
            } else {
                this.vm.get('FULLDOMAINNAME').enable();
                this.vm.get('OU').enable();
                this.vm.get('SYSTEMNAME').enable();
                this.vm.get('ISENABLEOVERWRITESAMEACCOUNT').enable();
            }

            // 加入中 退出中将再2s后继续查询
            if (this.data.DOMAINSTATUS === DomainStatusE.Joining || this.data.DOMAINSTATUS === DomainStatusE.Logouting) {
                setTimeout(() => this.getData(), 2 * 1000);
                return;
            }

            this.loading = false;
            this.loadFailed = false;
        }, () => {
            this.loading = false;
            this.loadFailed = true;
        });
    }

    checkDnsConfigure(vstoreId: string): Observable<boolean> {
        return this.fileDnsService.getDnsServer(vstoreId).pipe(
            map(({ data }) => {
                const ips = data.ADDRESS ? JSON.parse(data.ADDRESS) : [];
                return ips.filter(Boolean).length !== 0;
            }),
        );
    }

    /**
     * 配置dns
     */
    openDnsConfigure(): void {
        this.lazyloadService.appendComponent(ConfigureDnsComponent, {
            inSlideWindow: true,
            simpleModeStyle: this.simpleModeStyle,
            vstoreId: this.vstoreId,
            onClose: () => {
                this.checkDnsConfigure(this.vstoreId).subscribe((hasDns) => {
                    this.closeArgs.refresh = this.hasDns === hasDns;
                    this.hasDns = hasDns;
                });
            },
        });
    }

    /**
     * 加入域
     */
    joinDomain(): void {
        this.vm.validate((valid) => {
            if (!valid) {
                return;
            }

            const params: ServiceAd = {
                ADMINNAME: this.vm.value.ADMINNAME,
                ADMINPWD: this.vm.value.ADMINPWD,
                FULLDOMAINNAME: this.vm.value.FULLDOMAINNAME,
                OU: this.vm.value.OU,
                SYSTEMNAME: this.vm.value.SYSTEMNAME,
                ISENABLEOVERWRITESAMEACCOUNT: this.vm.value.ISENABLEOVERWRITESAMEACCOUNT,
            };

            this.service.joinDomain(this.vstoreId, params, { mask: true }).subscribe(() => {
                this.closeArgs.refresh = true;
                this.getData();
            });
        });
    }

    /**
     * 退出域
     */
    logoutDomain(): void {
        this.vm.validate((valid) => {
            if (!valid) {
                return;
            }

            this.promptService.warning({
                id: 'prompt-service-ad-logoutDomain',
                width: 700,
                title: this.i18n.logoutDomainBtn,
                content: this.i18n.exitDomainMsg,
                onOk: () => {
                    this.promptService.close();
                    const params: ServiceAd = {
                        ADMINNAME: this.vm.value.ADMINNAME,
                        ADMINPWD: this.vm.value.ADMINPWD,
                    };

                    this.service.logoutDomain(this.vstoreId, params, { mask: true }).subscribe(() => {
                        this.closeArgs.refresh = true;
                        this.getData();
                    });
                },
            });
        });
    }

    testFn(value: string): void {
        this.service.test(this.vstoreId, value,
            {
                mask: true,
                doSuccess: true,
                restNameParams: [value],
            },
        ).subscribe();
    }

    /**
     * 测试全域名
     */
    test(name: string): void {
        const control = this.vm.get(name) as FormControl;

        if (this.data.DOMAINSTATUS === DomainStatusE.Join && control.value) {
            this.testFn(control.value);
            return;
        }

        control.validate((valid) => {
            if (!valid) {
                return;
            }

            this.testFn(control.value);
        });
    }

    /**
     * 构建下发命令
     */
    buildCmds(): Command[] {
        const cmds: Command[] = [];
        return cmds;
    }
}
